import { formatCzk, formatUsd, JOB_PACK_DAYS, PRICES, PRICES_CZK } from '~/constants/pricing'
import { CS_TRANSLATION_NOTE, LEGAL_OPERATOR, operatorLineCs, operatorVatLineCs, type LegalDocument } from './operator'

/**
 * The Terms of Use in Czech, a translation of terms.ts. Section ids stay the
 * same in both languages. Keep it in step with terms.ts: change both in the
 * same commit.
 */
export function termsOfUseCs(): LegalDocument {
  const o = LEGAL_OPERATOR
  const mail = `[${o.email}](mailto:${o.email})`

  return {
    intro: [
      CS_TRANSLATION_NOTE,
      `Tyto podmínky používání upravují vaše používání ${o.brand}, online tvůrce CV dostupného na ${o.website.replace('https://', '')}, včetně webového CV, vizitky, motivačních dopisů a nástrojů AI (společně „Služba“). Používáním Služby nebo vytvořením účtu s těmito podmínkami souhlasíte. Pokud nesouhlasíte, Službu prosím nepoužívejte.`,
    ],
    sections: [
      {
        id: 'operator',
        title: '1. Kdo jsme',
        blocks: [
          `Službu provozuje: ${operatorLineCs()}`,
          'V těchto podmínkách „my“ znamená provozovatele a „vy“ osobu, která Službu používá.',
        ],
      },
      {
        id: 'eligibility',
        title: '2. Kdo může Službu používat a váš účet',
        blocks: [
          {
            list: [
              'K používání Služby vám musí být alespoň 16 let.',
              'Některé funkce fungují bez účtu. K ukládání CV, zveřejnění webového CV nebo koupi placeného tarifu potřebujete účet, který si můžete vytvořit s e-mailovou adresou nebo přes Google.',
              'Uvádějte nám pravdivé údaje a své přihlašovací údaje chraňte. Odpovídáte za vše, co se prostřednictvím vašeho účtu děje.',
              `Pokud máte podezření, že k vašemu účtu má přístup někdo jiný, dejte nám hned vědět na ${mail}.`,
            ],
          },
        ],
      },
      {
        id: 'service',
        title: '3. Služba a bezplatné používání',
        blocks: [
          'Služba vám umožňuje vytvářet, upravovat, ukládat a exportovat CV a motivační dopisy, zveřejnit webovou verzi CV a používat nástroje AI, které váš text navrhnou, vylepší nebo analyzují.',
          '**Bezplatné používání.** CV si můžete zdarma vytvořit a stáhnout jako PDF. Bezplatná PDF nesou vodoznak InMyResume. Importy s AI, exporty do PDF a některé další funkce jsou pro bezplatné uživatele a návštěvníky bez účtu omezeny denním limitem; aktuální limity jsou uvedeny v aplikaci.',
          '**Prémiové funkce.** PDF bez vodoznaku, vyšší nebo neomezené používání a další prémiové funkce popsané na [stránce s ceníkem](/pricing) jsou dostupné s placeným tarifem. „Neomezené“ znamená tolik, kolik potřebuje jeden člověk pro vlastní hledání práce: aby služba zůstala rychlá a dostupná, mají funkce AI štědrý denní limit férového užívání, který zastaví automatizované nebo sdílené používání.',
          'Službu neustále vylepšujeme, takže se funkce, šablony a limity mohou měnit. Funkci, za kterou jste už zaplatili, během zaplaceného období neodebereme, aniž bychom nabídli férovou náhradu nebo přiměřené vrácení peněz.',
        ],
      },
      {
        id: 'payments',
        title: '4. Placené tarify a platba',
        blocks: [
          `**Nákupy v korunách a ve zlotých prodáváme my.** Když web používáte v češtině, ceny vidíte a platíte v českých korunách; v polštině v polských zlotých, kartou, přes BLIK nebo Przelewy24. Takový nákup vám prodává provozovatel uvedený výše a vystaví vám doklad nebo fakturu; platbu zpracovává Stripe Payments Europe, Ltd. ${operatorVatLineCs()} Ceny v korunách a ve zlotých jsou proto konečné.`,
          '**Nákupy v dolarech prodává Lemon Squeezy.** Zpracovává je Lemon Squeezy LLC, která vystupuje jako prodejce (merchant of record). Zajišťuje pokladnu, platbu, fakturu a daň z obratu nebo DPH a na váš nákup se vztahují i podmínky Lemon Squeezy pro kupující zobrazené v pokladně. V pokladně může být podle místa vašeho bydliště připočtena daň.',
          'Aktuálně nabízíme tyto tarify:',
          {
            list: [
              `**Jednorázový export PDF (${formatCzk(PRICES_CZK.exportCredit)}, v dolarech ${formatUsd(PRICES.exportCredit)}).** Jednorázová platba za jedno stažení PDF bez vodoznaku. Kredit nepropadá.`,
              `**Job Search Pack (${formatCzk(PRICES_CZK.jobPack)}, v dolarech ${formatUsd(PRICES.jobPack)}).** Jednorázová platba za ${JOB_PACK_DAYS} dní Premium, počítaných od potvrzení platby. Automaticky se neobnovuje. Webové CV zveřejněné s balíčkem zůstane online po dobu jeho platnosti a po jeho skončení přejde do režimu offline; s dalším balíčkem ho můžete znovu zveřejnit.`,
              `**Roční Premium (${formatCzk(PRICES_CZK.yearly)} ročně, v dolarech ${formatUsd(PRICES.yearly)}).** Předplatné, které se **automaticky obnovuje** každý rok za cenu platnou v danou dobu, dokud ho nezrušíte.`,
            ],
          },
          'Předplatné můžete kdykoli zrušit odkazem v e-mailu s potvrzením od Lemon Squeezy (předplatné placené v korunách nebo ve zlotých tlačítkem „Spravovat předplatné a faktury“ na stránce Premium) nebo tím, že nám napíšete. Zrušení zastaví další obnovení; Premium zůstane aktivní do konce již zaplaceného období.',
          'Stávající předplatná tarifů, které už neprodáváme (například dřívější měsíční tarif), pokračují za podmínek sjednaných při nákupu a za tehdy sjednanou cenu, dokud je nezrušíte.',
          'Pokud změníme cenu předplatného, oznámíme vám to nejméně 30 dní předtím, než se změna projeví při dalším obnovení, abyste mohli předplatné předem zrušit.',
        ],
      },
      {
        id: 'refunds',
        title: '5. Právo na odstoupení a vrácení peněz',
        blocks: [
          'Pokud jste spotřebitel v EU, máte obvykle 14 dní na odstoupení od nákupu online. U digitálního obsahu a služeb, které začínají okamžitě, jako je export bez vodoznaku nebo přístup k Premium, nás v pokladně žádáte o okamžité zahájení a berete na vědomí, že zahájením plnění **ztrácíte právo na odstoupení**. Vaše práva v případě vadné Služby tím nejsou dotčena.',
          `Nad rámec zákona: pokud se něco pokazilo — například vám byla částka stržena dvakrát, předplatné se obnovilo omylem a od té doby jste Premium nepoužili, nebo placená funkce nefungovala — napište nám do 14 dnů od platby na ${mail}; situaci férově posoudíme a tam, kde je to odůvodněné, peníze vrátíme. Vrácené peníze přijdou na původní platební metodu: od Lemon Squeezy, u nákupu v korunách nebo ve zlotých od nás přes Stripe.`,
        ],
      },
      {
        id: 'your-content',
        title: '6. Váš obsah',
        blocks: [
          'Veškerá práva k obsahu, který do Služby vložíte — vašim textům, fotkám a údajům v CV („Váš obsah“) — vám zůstávají.',
          'Udělujete nám nevýhradní, celosvětovou a bezúplatnou licenci Váš obsah ukládat, zpracovávat, kopírovat a zobrazovat, a to jen v rozsahu nutném k provozu Služby pro vás — například k uložení vašeho CV, jeho převodu do PDF, jeho odeslání poskytovateli AI, když použijete funkci AI, nebo k zobrazení vašeho webového CV lidem, se kterými ho sdílíte. Licence zaniká, když obsah nebo svůj účet smažete, s výjimkou kopií v zálohách uchovávaných po omezenou dobu.',
          'Za Váš obsah odpovídáte vy. Dbejte na to, aby byl pravdivý, abyste měli právo ho použít (včetně fotky) a aby osobní údaje jiných lidí, například osob uvedených jako reference, byly uvedeny s jejich svolením.',
          '**Veřejné webové CV.** Pokud zveřejníte své webové CV nebo vizitku, kdokoli s odkazem si je může prohlédnout a vyhledávače je mohou indexovat. Zveřejnění můžete kdykoli zrušit. Zveřejnění je součástí Premium, takže webové CV přejde do režimu offline, když váš přístup k Premium skončí.',
          'Jak zacházíme s osobními údaji, popisují naše [Zásady ochrany osobních údajů](/privacy).',
        ],
      },
      {
        id: 'ai',
        title: '7. Funkce AI',
        blocks: [
          'Funkce AI generují návrhy automaticky pomocí modelů třetích stran. Výsledky mohou být nepřesné, neúplné nebo nevhodné pro vaši situaci. **Výstup AI si vždy před použitím zkontrolujte** a nepřidávejte zkušenosti, kvalifikace ani úspěchy, které nemáte.',
          'Skóre ATS kontroly a shody s pozicí jsou odhady, které vám pomáhají CV zlepšit. Nepředpovídají, jak vaši přihlášku ohodnotí kterýkoli zaměstnavatel nebo systém pro sledování uchazečů, a nezaručujeme pohovory, pracovní nabídky ani žádný jiný výsledek.',
        ],
      },
      {
        id: 'acceptable-use',
        title: '8. Přípustné používání',
        blocks: [
          'Nesmíte:',
          {
            list: [
              'používat Službu k vytváření nepravdivých nebo zavádějících dokumentů určených k oklamání zaměstnavatelů či jiných osob nebo k vydávání se za jinou osobu;',
              'nahrávat obsah, který je nezákonný, pomlouvačný, diskriminační, sexuálně explicitní nebo porušuje práva někoho jiného;',
              'zveřejňovat osobní údaje jiných lidí bez právního základu;',
              'obcházet limity používání, vodoznaky nebo platby, například vytvářením více účtů nebo odstraňováním vodoznaků;',
              've velkém rozsahu stahovat, kopírovat nebo přeprodávat Službu, její šablony nebo výstupy AI, nebo ji používat k vývoji konkurenčního produktu;',
              'zkoumat zranitelnosti Služby, přetěžovat ji nebo na ni útočit, nebo přistupovat k účtům či údajům, které vám nepatří;',
              'používat Službu způsobem, který porušuje zákon nebo pravidla našich poskytovatelů, včetně zásad používání OpenAI.',
            ],
          },
        ],
      },
      {
        id: 'our-rights',
        title: '9. Naše duševní vlastnictví',
        blocks: [
          'Služba, její design, kód, šablony CV, texty, loga a název InMyResume patří nám nebo našim poskytovatelům licencí. Šablony můžete používat k vytváření CV a motivačních dopisů pro sebe a výsledné dokumenty volně sdílet nebo tisknout. Šablony ani jiné části Služby nesmíte kopírovat k žádnému jinému účelu.',
        ],
      },
      {
        id: 'availability',
        title: '10. Dostupnost',
        blocks: [
          'Usilujeme o to, aby byla Služba dostupná a vaše údaje v bezpečí, nemůžeme však zaručit nepřerušený ani bezchybný provoz. Údržba, výpadky poskytovatelů nebo události mimo naši kontrolu mohou způsobit dočasnou nedostupnost. Důležité dokumenty si prosím uchovávejte i u sebe, například stažením PDF.',
        ],
      },
      {
        id: 'liability',
        title: '11. Odpovědnost',
        blocks: [
          'Nic v těchto podmínkách neomezuje odpovědnost, kterou podle zákona omezit nelze, včetně odpovědnosti za úmysl nebo hrubou nedbalost, ani vaše zákonná práva spotřebitele.',
          'Jinak neodpovídáme za nepřímou nebo následnou škodu, jako je ušlý výdělek nebo zmeškané pracovní příležitosti, a naše celková odpovědnost vůči vám za jakýkoli nárok související se Službou je omezena částkou, kterou jste nám zaplatili během 12 měsíců před vznikem nároku, nebo 50 USD, pokud jste nezaplatili nic.',
        ],
      },
      {
        id: 'termination',
        title: '12. Ukončení používání',
        blocks: [
          `Používání Služby můžete kdykoli ukončit. Chcete-li smazat svůj účet a obsah, napište nám na ${mail} z e-mailové adresy propojené s vaším účtem. Smazání účtu automaticky neruší aktivní předplatné; zrušte ho prosím nejdříve.`,
          'Účet můžeme pozastavit nebo zrušit, pokud závažně nebo opakovaně porušujete tyto podmínky nebo pokud to vyžaduje zákon. Pokud porušení není závažné nebo naléhavé, nejdříve vás upozorníme a dáme vám možnost stáhnout si obsah. Pokud váš účet zrušíme, aniž byste podmínky porušili, vrátíme vám nevyčerpanou část předplaceného období.',
        ],
      },
      {
        id: 'changes',
        title: '13. Změny těchto podmínek',
        blocks: [
          'Tyto podmínky můžeme aktualizovat, když se změní Služba nebo zákon. Datum nahoře ukazuje nejnovější verzi. O podstatných změnách vás budeme informovat e-mailem nebo v aplikaci nejméně 14 dní před jejich účinností. Pokud nesouhlasíte, můžete Službu přestat používat a zrušit předplatné dříve, než se změna uplatní.',
        ],
      },
      {
        id: 'law',
        title: '14. Rozhodné právo a spory',
        blocks: [
          'Tyto podmínky se řídí právem České republiky. Pokud jste spotřebitel, zůstává vám i ochrana kogentních předpisů země, kde žijete, a nárok můžete uplatnit u tamních soudů.',
          `Nejdříve nás prosím kontaktujte na ${mail}; většinu věcí lze vyřešit rychle. Spotřebitelé v České republice se mohou obrátit také na Českou obchodní inspekci ([coi.cz](https://www.coi.cz)) k mimosoudnímu řešení spotřebitelských sporů.`,
        ],
      },
      {
        id: 'contact',
        title: '15. Kontakt',
        blocks: [
          `Máte dotazy k těmto podmínkám? Napište na ${mail} nebo použijte náš [kontaktní formulář](/contact).`,
        ],
      },
    ],
  }
}
